"use client";

import { Check, X } from "lucide-react";
import type { ChipVariant, ColumnFormatOptions, ColumnValueFormat, SelectListItem } from "../domain";
import { COLUMN_VALUE_FORMAT_OBJ } from "../domain";
import { DEFAULT_LOCALE, formatArrayValue, formatCurrencyValue, formatDateTimeValue, formatDateValue, formatNumberValue, formatPercentValue, formatTextValue } from "../app";
import { useTableComponents } from "./table-provider";


function formatBooleanValue(v: unknown): React.ReactNode {
  if (v === true) return <Check className="h-4 w-4 text-success" />;
  if (v === false) return <X className="h-4 w-4 text-danger" />;
  return String(v ?? "—");
}

function formatIdValue(v: unknown): React.ReactNode {
  if (!v) return "—";
  return (
    <span className="font-mono text-xs text-text-secondary">{String(v)}</span>
  );
}

function ChipValue({ variant, label }: { variant: ChipVariant; label: string }) {
  const { Badge } = useTableComponents();
  return <Badge variant={variant}>{label}</Badge>;
}

function formatChipValue(
  v: unknown,
  opts: ColumnFormatOptions | undefined,
  listItems: SelectListItem[] | undefined,
  locale: string,
): React.ReactNode {
  if (!v) return "—";
  const key = String(v);
  const variant = opts?.variants?.[key] ?? "secondary";
  const item = listItems?.find((i) => i.value === key);
  const label = item?.label?.[locale] ?? key;
  return <ChipValue variant={variant} label={label} />;
}
export function formatCell(
  v: unknown,
  format: ColumnValueFormat,
  opts?: ColumnFormatOptions,
  listItems?: SelectListItem[],
  locale: string = DEFAULT_LOCALE,
): React.ReactNode {
  switch (format) {
    case COLUMN_VALUE_FORMAT_OBJ.DATE:
      return formatDateValue(v);
    case COLUMN_VALUE_FORMAT_OBJ.DATETIME:
      return formatDateTimeValue(v);
    case COLUMN_VALUE_FORMAT_OBJ.BOOLEAN:
      return formatBooleanValue(v);
    case COLUMN_VALUE_FORMAT_OBJ.PERCENT:
      return formatPercentValue(v, opts, locale);
    case COLUMN_VALUE_FORMAT_OBJ.NUMBER:
      return formatNumberValue(v, opts, locale);
    case COLUMN_VALUE_FORMAT_OBJ.CURRENCY:
      return formatCurrencyValue(v, opts, locale);
    case COLUMN_VALUE_FORMAT_OBJ.ARRAY:
      return formatArrayValue(v, opts);
    case COLUMN_VALUE_FORMAT_OBJ.ID:
      return formatIdValue(v);
    case COLUMN_VALUE_FORMAT_OBJ.CHIP:
      return formatChipValue(v, opts, listItems, locale);
    case COLUMN_VALUE_FORMAT_OBJ.TEXT:
      return formatTextValue(v);
    default:
      return String(v ?? "—");
  }
}

